import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { LoadingOutlined } from '@ant-design/icons';
import { Checkbox, Spin, message } from 'antd';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { T } from 'react-translator-component';
import styled from 'styled-components';

import { combiningObjects } from './listFormater';
import OcppTagCardInnerContent from './OcppTagCardInnerContent/OcppTagCardInnerContent';

import { getOcppTagsCards } from '../../../hooks/ocpptags';
import { createXlsxOcppTagReport } from '../../../hooks/reports';
import { getTenants } from '../../../hooks/tenants';
import { OcppTag } from '../../../types/responses/OcppTags';
import {
  normalOrder,
  twoOrMoreWordsOrder,
} from '../../../utils/columnHeaderOrder';
import { setIconActive } from '../../../utils/list';
import { modalErrorReport } from '../../../utils/modalErrorReport/modalErrorReport';
import {
  ocppTagListFilter,
  ocppTagFilter,
} from '../../../utils/ocppTags/ocppTagListFilter';
import { parserTenantsForSelect } from '../../../utils/stations/stationSelect';
import { COLORS } from '../../../utils/theme/colors';
import { HeaderIcon } from '../../molecules/HeaderIcon/HeaderIcon';
import { ListSkeleton } from '../../molecules/ListSkeleton/ListSkeleton';
import { Search } from '../../molecules/Search';
import { Select } from '../../organisms/Select';
import CardListConstructor from '../CardListConstructor/CardListConstructor';
import { closeAllCards } from '../CardListConstructor/helpers';
import EmptySearch from '../EmptySearch';

interface IOcppTagsTableTable {
  routes: any;
}

const OcppTagsTable: React.FC<IOcppTagsTableTable> = ({ routes }) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [data, setdata] = useState<any>([]);
  const [paginationData, setpaginationData] = useState<any>([]);
  const [searchInput, setSearchInput] = useState('');
  const [tenantList, setTenantList] = useState<any>([]);
  const [ocppTagList, setOcppTagList] = useState<OcppTag[]>([]);
  const [tenantInputValue, setTenantInputValue] = useState<any>('');
  const [isExcelLoading, setIsExcelLoading] = useState(false);
  const [showOnlyActiveTags, setShowOnlyActiveTags] = useState(true);

  const childRef: any = useRef();
  const paginate = () => childRef?.current?.paginate();

  const antLoadingIcon = <LoadingOutlined style={{ fontSize: 24 }} spin />;

  const HEADER = useMemo(
    () => [
      {
        text: T('Tag ID'),
        sideIcon: <HeaderIcon />,
        action: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) =>
          twoOrMoreWordsOrder(
            'idTag',
            ocppTagListFilter(searchInput, ocppTagList),
            (newList: OcppTag[]) => {
              setIconActive(e);
              closeAllCards();
              paginate();
              setdata(combiningObjects(newList, HEADER));
              setpaginationData(newList);
            },
            e,
          ),
        size: '25%',
        dropdown: <></>,
      },
      {
        text: T('Type'),
        sideIcon: <HeaderIcon />,
        action: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) =>
          normalOrder(
            'type',
            ocppTagListFilter(searchInput, ocppTagList),
            (newList: OcppTag[]) => {
              setIconActive(e);
              closeAllCards();
              paginate();
              setdata(combiningObjects(newList, HEADER));
              setpaginationData(newList);
            },
            e,
          ),
        size: '15%',
        dropdown: <></>,
      },
      {
        text: T('Card User'),
        sideIcon: <HeaderIcon />,
        action: (e: React.MouseEvent<HTMLDivElement>) =>
          normalOrder(
            'parentUserName',
            ocppTagListFilter(searchInput, ocppTagList),
            (newList: OcppTag[]) => {
              setIconActive(e);
              closeAllCards();
              paginate();
              setdata(combiningObjects(newList, HEADER));
              setpaginationData(newList);
            },
            e,
          ),
        size: '25%',
        dropdown: <></>,
      },
      {
        text: T('Account'),
        sideIcon: <HeaderIcon />,
        action: (e: React.MouseEvent<HTMLDivElement>) =>
          normalOrder(
            'tenant',
            ocppTagListFilter(searchInput, ocppTagList),
            (newList: OcppTag[]) => {
              setIconActive(e);
              closeAllCards();
              paginate();
              setdata(combiningObjects(newList, HEADER));
              setpaginationData(newList);
            },
            e,
          ),
        size: '25%',
        dropdown: <></>,
      },
      {
        text: T('Charging'),
        sideIcon: <></>,
        action: () => {},
        size: '10%',
        dropdown: <></>,
      },
    ],
    [ocppTagList, searchInput],
  );

  const fetchOcppTags = useCallback(async () => {
    try {
      setLoading(true);
      const ocppTags = await getOcppTagsCards('CARD', null, showOnlyActiveTags);
      setOcppTagList(ocppTags?.ocppTagList);

      const tenants = await getTenants();
      setTenantList(tenants.list);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.error(error);
    }
  }, [showOnlyActiveTags]);

  useEffect(() => {
    fetchOcppTags();
  }, [fetchOcppTags]);

  type FilterOcppTags = (e: any) => void;
  const filterUsers: FilterOcppTags = useCallback((value) => {
    setSearchInput(value);
  }, []);

  const clearSearch = () => {
    setSearchInput('');
  };

  const handleFilters = useCallback(() => {
    setpaginationData(
      ocppTagFilter(
        ocppTagListFilter(searchInput, ocppTagList),
        tenantInputValue,
      ),
    );
    setdata(
      combiningObjects(
        ocppTagFilter(
          ocppTagListFilter(searchInput, ocppTagList),
          tenantInputValue,
        ),
        HEADER,
      ),
    );
  }, [HEADER, ocppTagList, searchInput, tenantInputValue]);

  useEffect(() => {
    handleFilters();
  }, [handleFilters]);

  useEffect(() => {
    paginate();
  }, [paginationData]);

  const handleDownloadExcel = async () => {
    setIsExcelLoading(true);
    const ocppTagForm = {
      tenantPk: tenantList
        .filter((tenant) => tenant.name == tenantInputValue)
        .map((tenant) => tenant.tenant_pk),
      searchInput: searchInput,
    };
    const xlsxOcppTagsReport = await createXlsxOcppTagReport(ocppTagForm);

    if (xlsxOcppTagsReport.type === 'error') {
      if (xlsxOcppTagsReport.status === 403) {
        modalErrorReport();
      } else {
        message.error(T('Unable to download the .xlsx file.'));
      }
    }
    setIsExcelLoading(false);
  };

  return (
    <Div>
      <div className='tools_bar'>
        <h1 className='page_title'>{T('Ocpp Tags')}</h1>

        <SelectTenantWrapper>
          <Select
            value={tenantInputValue}
            options={parserTenantsForSelect(tenantList)}
            action={(value: string) => setTenantInputValue(value)}
            ordered
          />
        </SelectTenantWrapper>

        <Checkbox
          className='active_tag'
          checked={showOnlyActiveTags}
          onChange={(e) => setShowOnlyActiveTags(e.target.checked)}
        >
          {T('Active Tags')}
        </Checkbox>

        <Search
          className='search_input'
          placeholder={T('Search By TAG ID')}
          action={filterUsers}
          closeBtn={clearSearch}
          value={searchInput}
        />

        {routes && routes.find(({ path }) => path === '/ocpptags/unknown') ? (
          <Link className='action_button cursor' to='/ocpptags/unknown'>
            {T('Tags Desconhecidas')}
          </Link>
        ) : null}

        {isExcelLoading ? (
          <div style={{ margin: '10px 20px 0 50px' }}>
            <Spin indicator={antLoadingIcon} />
          </div>
        ) : (
          <button
            className='action_button cursor'
            onClick={() => handleDownloadExcel()}
          >
            {T('Download Excel')}
          </button>
        )}

        {routes && routes.find(({ path }) => path === '/ocpptags/create') ? (
          <Link className='action_button cursor' to='/ocpptags/create'>
            {T('New OCPP Tag')} +
          </Link>
        ) : null}
      </div>
      {loading ? (
        <ListWrapper>
          <ListSkeleton style={{ paddingTop: 20 }} />
        </ListWrapper>
      ) : !loading && !data?.cardsList?.length ? (
        <EmptySearch />
      ) : (
        <ListWrapper>
          <CardListConstructor
            childRef={childRef}
            data={data}
            InnerComponent={OcppTagCardInnerContent}
            pureData={ocppTagListFilter(searchInput, paginationData)}
            paginationCallback={(newList: OcppTag[]) =>
              setdata(combiningObjects(newList, HEADER))
            }
          />
        </ListWrapper>
      )}
    </Div>
  );
};

const mapStateToProps = (state: { user: { routes: any } }) => ({
  routes: state.user.routes,
});

export default connect(mapStateToProps)(OcppTagsTable);

const ListWrapper = styled.div`
  padding-left: 24px;
  padding-right: 24px;
`;

const Div = styled.div`
  padding-bottom: 70px;

  .search_input {
    flex: 1 1 250px;
    min-width: 180px;
    max-width: 350px;
    margin-top: 23px;
    margin-right: 20px;
  }

  .search_input input {
    box-shadow: 0 0 10px 1px rgba(0, 0, 0, 0.1);
    padding: 0 70px 0 30px;
    width: 100%;
    border-radius: 8px;
  }

  .tools_bar {
    display: flex;
    align-items: center;
    gap: 5px;
    flex-wrap: nowrap;

    .page_title {
      flex: auto;
      margin: 20px 0 0 2%;
      font-size: 21px;
      font-weight: normal;
      font-family: 'Roboto';
      color: #777;
    }
  }

  .active_tag {
    margin: 20px 0 0 0;
    padding: 5px 15px;
    border-radius: 8px;
    font-size: 13px;
    text-decoration: none;
  }

  .action_button {
    background-color: ${COLORS.transparentDark};
    margin: 20px 10px 0 0;
    padding: 10px 30px;
    border-radius: 8px;
    font-size: 13px;
    color: #444;
    text-decoration: none;
    transition: background-color 200ms ease-in-out;

    :focus {
      outline: none;
    }

    :hover {
      background-color: ${({ theme }) => theme.hoveredDiv};
      color: ${COLORS.primary};
    }
  }

  .card_in_list.gabrr_expanded {
    height: 175px;
  }
`;

const SelectTenantWrapper = styled.div`
  min-width: 180px;
  margin: 20px 50px 0 0;
`;
